import { Component, Input, OnInit } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { finalize } from "rxjs/operators";
import { forkJoin } from "rxjs";
import { ToastService } from "src/app/core";
import { ConfigurationApi, DocumentTypes, GovernanceDocumentRequestTypeMappingDTO, LookupApi, LookupField } from "src/app/core/api.gen";
import { LoaderService } from "src/app/core/loader.service";
import { LookupTypeKeys } from "src/app/shared/lookup-type-keys";

@Component({
  selector: "eos-governance-doc-mapping-edit",
  templateUrl: "./governance-doc-mapping-edit.component.html",
})
export class GovernanceDocMappingEditComponent implements OnInit {
  @Input() activity: GovernanceDocumentRequestTypeMappingDTO;
  @Input() documentTypes: DocumentTypes[] = [];
  @Input() isEdit: boolean = false;

  documentValues: LookupField[] = [];
  documentNames: LookupField[] = [];
  documentNamesFiltered: LookupField[] = [];

   private originalValue: any;

  get isEditMode(): boolean {
    return this.activity !== undefined && this.isEdit;
  }

  createForm = new UntypedFormGroup({
    id: new UntypedFormControl(0),
    documentTypeId: new UntypedFormControl(null, Validators.required),
    documentValueId: new UntypedFormControl(null, Validators.required),
    documentNameId: new UntypedFormControl(null, Validators.required),
    isActive: new UntypedFormControl(true),
    isMandatory: new UntypedFormControl(false),
    instruction: new UntypedFormControl(null),
  });

  constructor(
    public modal: NgbActiveModal,
    private loader: LoaderService,
    private toast: ToastService,
    private lookupApi: LookupApi,
    private configApi: ConfigurationApi
  ) {}

  ngOnInit() {
    const selectedId = this.isEditMode ? this.activity?.documentTypeId : null;
    this.documentTypes = this.documentTypes.filter(t => t.isActive || t.id === selectedId);

    this.loader.show();
    forkJoin({
      documentValues: this.lookupApi.getLookupFieldsByTypes([LookupTypeKeys.DOCUMENT_TYPES]),
      documentNames: this.lookupApi.getLookupFieldsByTypes([LookupTypeKeys.DOCUMENT_NAMES]),
    })
      .pipe(finalize(() => this.loader.clear()))
      .subscribe((p) => {
        this.documentValues = p.documentValues;
        this.documentNames = p.documentNames;

        if (this.isEditMode) {
          this.bindActivityInfo(this.activity);
        }
        this.originalValue = this.createForm.getRawValue();
      });
  }

  get hasChanges(): boolean {
    return JSON.stringify(this.createForm.getRawValue()) !== JSON.stringify(this.originalValue);
  }

  bindActivityInfo(a: GovernanceDocumentRequestTypeMappingDTO) {
    this.documentNamesFiltered = a.documentValueId
      ? this.documentNames.filter(n => n.parentFieldId === a.documentValueId)
      : [];
    this.createForm.patchValue({
      id: a.id,
      documentTypeId: a.documentTypeId ?? null,
      documentValueId: a.documentValueId ?? null,
      documentNameId: a.documentNameId ?? null,
      isActive: a.isActive ?? true,
      isMandatory: a.isMandatory ?? false,
      instruction: a.instruction ?? null,
    });
  }

  onDocumentValueChange(event: any) {
    this.createForm.controls['documentNameId'].reset();
    this.documentNamesFiltered = event
      ? this.documentNames.filter(n => n.parentFieldId === event.id)
      : [];
  }

  saveActivity() {
    if (this.createForm.invalid) return;

    const form = this.createForm.getRawValue();
    const selectedDocumentValue = this.documentValues.find(x => x.id === form.documentValueId);
    const selectedDocumentName = this.documentNames.find(x => x.id === form.documentNameId);

    const payload = new GovernanceDocumentRequestTypeMappingDTO({
      id: form.id ?? 0,
      documentTypeId: form.documentTypeId,
      documentValueId: form.documentValueId,
      documentValue: selectedDocumentValue?.displayText,
      documentNameId: form.documentNameId,
      documentDisplayText: selectedDocumentName?.displayText,
      isActive: !!form.isActive,
      isMandatory: !!form.isMandatory,
      instruction: form.instruction,
    });

    this.loader.show();
    this.configApi
      .saveGovernanceDocumentRequestTypeMapping(payload)
      .pipe(finalize(() => this.loader.clear()))
      .subscribe(
        (result) => {
          if (result?.statusCode === 200 || result?.statusCode === 201) {
            this.toast.success(result.message || "Saved successfully");
            this.modal.close("success");
            return;
          }

          this.toast.error(result?.message || "Error occurred, please try again!");
        },
        (error) => this.toast.serverError(error, "Error occurred, please try again!")
      );
  }
}